import type { Goal } from '../../types/timeline'
import type { AiMagicChatTurn } from '../../services/aiService'
import type { useNls } from '../../nls/nlsProvider'

export const AI_FALLBACK_NOTE = 'I have enough context.'

export const formatProposalDate = (value?: string | null): string => {
  if (!value) return ''
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/**
 * Build the recap payload for a P3 PROPOSAL_SUMMARY turn. Rendered as a
 * bulleted summary chip beneath the assistant bubble with Build / Change
 * chips. The goal itself is stashed in `pendingPreviewsRef` by
 * `applyAiJobOutcome` — Build reuses that stash, no re-generation.
 */
export const buildProposalSummaryForGoal = (
  goal: Goal,
  aiNote: string | null,
  t: ReturnType<typeof useNls>['t'],
  displayedMessage?: string | null,
): NonNullable<AiMagicChatTurn['proposalSummary']> => {
  const tasks = goal.tasks ?? []
  const subtaskCount = tasks.reduce((count, task) => count + (task.subTasks?.length ?? 0), 0)
  const checklistCount = (goal.checklist ?? []).length
    + tasks.reduce((count, task) => count + (task.checklist?.length ?? 0), 0)
  const bullets: string[] = []
  if (goal.startDate && goal.endDate) {
    const startStr = formatProposalDate(String(goal.startDate))
    const endStr = formatProposalDate(String(goal.endDate))
    bullets.push(t('NLSAiProposalTimelineBullet', { start: startStr, end: endStr }))
  }
  const scopeParts = [
    t(tasks.length === 1 ? 'NLSAiProposalTaskCountOne' : 'NLSAiProposalTaskCountMany', { count: tasks.length }),
    subtaskCount > 0 ? t(subtaskCount === 1 ? 'NLSAiProposalSubtaskCountOne' : 'NLSAiProposalSubtaskCountMany', { count: subtaskCount }) : null,
    checklistCount > 0 ? t(checklistCount === 1 ? 'NLSAiProposalChecklistCountOne' : 'NLSAiProposalChecklistCountMany', { count: checklistCount }) : null,
  ].filter(Boolean)
  bullets.push(t('NLSAiProposalScopeBullet', { scope: scopeParts.join(', ') }))
  if (goal.description) {
    const trimmedDescription = goal.description.trim().replace(/\s+/g, ' ')
    if (trimmedDescription) {
      bullets.push(trimmedDescription.length > 180
        ? `${trimmedDescription.slice(0, 177)}…`
        : trimmedDescription)
    }
  }
  if (aiNote && aiNote.trim().length > 0) {
    const trimmedNote = aiNote.trim().replace(/\s+/g, ' ')
    const trimmedDesc = goal.description?.trim().replace(/\s+/g, ' ')
    const trimmedDisp = displayedMessage?.trim().replace(/\s+/g, ' ')
    const isFallback = trimmedNote.toLowerCase() === AI_FALLBACK_NOTE.toLowerCase()
    const isDuplicateOfDesc = Boolean(trimmedDesc && trimmedNote === trimmedDesc)
    const isDuplicateOfMessage = Boolean(trimmedDisp && trimmedNote === trimmedDisp)

    if (!isFallback && !isDuplicateOfDesc && !isDuplicateOfMessage) {
      bullets.push(trimmedNote.length > 220 ? `${trimmedNote.slice(0, 217)}…` : trimmedNote)
    }
  }
  return {
    heading: goal.title?.trim() || t('NLSAiProposalReadyHeading'),
    bullets: bullets.slice(0, 5),
    buildPrompt: t('NLSAiProposalBuildPrompt'),
  }
}
